// src/pages/goodsReceivedNote/GoodsReceivedNoteDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, PackageCheck, Package, Printer, XCircle, Undo2, FileText, Building2, Warehouse, Calendar, Truck, User } from 'lucide-react';
import { PageHeader, PrintPreviewModal, InfoCard } from '../../components/common';
import { getGoodsReceivedNote, cancelGoodsReceivedNote } from '../../services/api/procurement/grn';
import { getPurchaseOrderById } from '../../services/api/purchaseOrder';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDateTime } from '../../utils/dateUtils';
import { exportGoodsReceivedNotePDF } from '../../utils/offlineOrderPdfExporter';
import { getCssVar } from '../../utils/themeColors';
import { PURCHASE_ORDER_BASE_PATH, buildProcurementCreatePath, buildReturnState } from '../../utils/procurementNavigation';
import GoodsReceivedNoteA4Document from './GoodsReceivedNoteA4Document';
import ProductThumb from '../procurement/components/ProductThumb';
import ps from '../procurement/components/ProcurementDetails.module.css';
import styles from './GoodsReceivedNoteDetailsPage.module.css';

const STATUS_COLORS = {
  received: 'success',
  cancelled: 'error',
};

const Badge = ({ type, label }) => (
  <span className={`${styles.badge} ${styles[`badge_${type}`]}`}>{label}</span>
);

const itemName = (item) => {
  const product = item.purchaseOrderItem?.Product;
  const variation = item.purchaseOrderItem?.ProductVariation;
  if (!product) return `Item #${item.purchaseOrderItemId}`;
  return `${product.name}${variation?.sku ? ` — ${variation.sku}` : ''}`;
};

const GoodsReceivedNoteDetailsPage = () => {
  const { grnId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error } = useAlert();

  const [grn, setGrn] = useState(null);
  const [purchaseOrder, setPurchaseOrder] = useState(null); // full PO fetch — GRN's own PO include has no Supplier
  const [loading, setLoading] = useState(true);
  const [showPrint, setShowPrint] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  const loadGrn = async () => {
    try {
      setLoading(true);
      const result = await getGoodsReceivedNote(grnId);
      setGrn(result.goodsReceivedNote);

      if (result.goodsReceivedNote?.purchaseOrder?.id) {
        try {
          const { purchaseOrder: fullPo } = await getPurchaseOrderById(result.goodsReceivedNote.purchaseOrder.id);
          setPurchaseOrder(fullPo);
        } catch (err) {
          console.error('Failed to load purchase order details:', err);
        }
      }
    } catch (err) {
      error(err?.response?.data?.message || t('goodsReceivedNote.failedToLoadGoodsReceivedNote'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadGrn(); }, [grnId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrint = () => setShowPrint(true);

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      await cancelGoodsReceivedNote(grnId, { cancellationReason: cancellationReason.trim() || undefined });
      success(t('goodsReceivedNote.goodsReceivedNoteCancelledSuccessfully'));
      setShowCancelModal(false);
      setCancellationReason('');
      loadGrn();
    } catch (err) {
      error(err?.response?.data?.message || t('goodsReceivedNote.failedToCancelGoodsReceivedNote'));
    } finally {
      setIsCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (!grn) {
    return <div className={styles.notFound}>{t('errors.notFound')}</div>;
  }

  const renderContent = () => (
    <div className={styles.grid}>
      <div className={styles.column}>
        <InfoCard icon={PackageCheck} title={t('goodsReceivedNote.grnNumber')}>
          <div className={ps.infoGrid}>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('common.status')}</span>
              <span className={ps.infoValue}>
                <Badge type={STATUS_COLORS[grn.status]} label={t(`goodsReceivedNote.status_${grn.status}`)} />
              </span>
            </div>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('goodsReceivedNote.purchaseOrderNumber')}</span>
              <span className={ps.infoValue}>
                <FileText size={14} />
                {grn.purchaseOrder?.purchaseOrderNumber ? (
                  <Link to={`${PURCHASE_ORDER_BASE_PATH}/${grn.purchaseOrder.id}`}>{grn.purchaseOrder.purchaseOrderNumber}</Link>
                ) : t('common.na')}
              </span>
            </div>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('goodsReceivedNote.supplier')}</span>
              <span className={ps.infoValue}><Building2 size={14} />{purchaseOrder?.Supplier?.name || t('common.na')}</span>
            </div>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('goodsReceivedNote.warehouse')}</span>
              <span className={ps.infoValue}><Warehouse size={14} />{grn.Warehouse?.name || t('common.na')}</span>
            </div>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('goodsReceivedNote.receivedAt')}</span>
              <span className={ps.infoValue}><Calendar size={14} />{grn.receivedAt ? formatDateTime(grn.receivedAt) : t('common.na')}</span>
            </div>
            {grn.supplierDeliveryReference && (
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('goodsReceivedNote.supplierDeliveryReference')}</span>
                <span className={ps.infoValue}><Truck size={14} />{grn.supplierDeliveryReference}</span>
              </div>
            )}
            {grn.notes && (
              <div className={`${ps.infoItem} ${ps.fullWidth}`}>
                <span className={ps.infoLabel}>{t('goodsReceivedNote.notes')}</span>
                <p className={ps.notesText}>{grn.notes}</p>
              </div>
            )}
          </div>
        </InfoCard>

        <InfoCard icon={FileText} title={t('ecomOrders.additionalInformation')}>
          <div className={ps.infoList}>
            <div className={ps.infoListItem}>
              <label>{t('goodsReceivedNote.receivedBy')}</label>
              <p><User size={14} />{grn.receivedBy?.name || t('common.na')}</p>
            </div>
            <div className={ps.infoListItem}>
              <label>{t('common.created')}</label>
              <p><Calendar size={14} />{formatDateTime(grn.createdAt)}</p>
            </div>
          </div>
        </InfoCard>
      </div>

      <InfoCard icon={Package} title={t('goodsReceivedNote.itemsReceived')}>
        <div className={ps.tableWrapper}>
          <table className={ps.itemsTable}>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>#</th>
                <th>{t('goodsReceivedNote.item')}</th>
                <th className={ps.alignRight}>{t('goodsReceivedNote.quantityReceived')}</th>
              </tr>
            </thead>
            <tbody>
              {(grn.items || []).map((item, index) => {
                const poItem = item.purchaseOrderItem;
                const sku = poItem?.ProductVariation?.sku || poItem?.Product?.sku;
                return (
                  <tr key={item.id}>
                    <td className={ps.itemIndexCell}>{index + 1}</td>
                    <td>
                      <div className={ps.productCellInner}>
                        <ProductThumb item={poItem} alt={poItem?.Product?.name || ''} />
                        <div className={ps.productCell}>
                          <span className={ps.productName}>{poItem?.Product?.name || itemName(item)}</span>
                          {sku && <span className={ps.productSku}>SKU: {sku}</span>}
                        </div>
                      </div>
                    </td>
                    <td className={`${ps.alignRight} ${ps.amountCell} ${ps.amountStrong}`}>{item.quantityReceived}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </InfoCard>
    </div>
  );

  return (
    <div className={styles.page}>
      <PageHeader
        className={styles.detailHeader}
        title={grn.grnNumber}
        icon={null}
        breadcrumbs={
          <button onClick={() => navigate('/procurement/grn')} className={styles.backBtn}>
            <ChevronLeft size={16} /> {t('goodsReceivedNote.allGoodsReceivedNotes')}
          </button>
        }
        actions={
          <div className={styles.headerActions}>
            {grn.status !== 'cancelled' && hasPermission('create_rtv') && (
              <Link
                className={`${styles.secondaryBtn} ${ps.toneWarning}`}
                to={buildProcurementCreatePath('/procurement/return-to-vendor/create', { goodsReceivedNoteId: grn.id }, `/procurement/grn/${grn.id}`, grn.grnNumber)}
                state={buildReturnState(`/procurement/grn/${grn.id}`, grn.grnNumber)}
              >
                <Undo2 size={15} /> {t('returnToVendor.createReturnToVendor')}
              </Link>
            )}
            {grn.status !== 'cancelled' && hasPermission('cancel_grn') && (
              <button className={`${styles.secondaryBtn} ${ps.toneDanger}`} onClick={() => setShowCancelModal(true)}>
                <XCircle size={15} /> {t('common.cancel')}
              </button>
            )}
            <button className={styles.secondaryBtn} onClick={handlePrint}>
              <Printer size={15} /> {t('common.printReceipt', 'Print Receipt')}
            </button>
          </div>
        }
      />

      {renderContent()}

      {showCancelModal && (
        <div className={styles.overlay}>
          <div className={styles.smallModal}>
            <div className={styles.modalHeader}>
              <h3>{t('goodsReceivedNote.cancelGoodsReceivedNote')}</h3>
            </div>
            <div className={styles.modalBody}>
              <p>{t('goodsReceivedNote.confirmCancel')}</p>
              <label>{t('goodsReceivedNote.cancellationReason')}</label>
              <textarea
                className={styles.select}
                rows={3}
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder={t('goodsReceivedNote.cancellationReasonPlaceholder')}
              />
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setShowCancelModal(false)} disabled={isCancelling}>
                {t('common.cancel')}
              </button>
              <button className={styles.primaryBtn} onClick={handleCancel} disabled={isCancelling}>
                {isCancelling ? t('common.loading') : t('goodsReceivedNote.cancelGoodsReceivedNote')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPrint && (
        <PrintPreviewModal
          title={`Goods Received Note — ${grn.grnNumber}`}
          onClose={() => setShowPrint(false)}
          pdfFileName={`Goods_Received_Note_${grn.grnNumber || grn.id}.pdf`}
          onDownloadPdf={() => exportGoodsReceivedNotePDF(grn, purchaseOrder, {
            primary: getCssVar('--color-primary', '#4f46e5'),
            primaryDark: getCssVar('--color-primary-dark', '#3730a3'),
          })}
          renderA4={() => <GoodsReceivedNoteA4Document grn={grn} purchaseOrder={purchaseOrder} />}
        />
      )}
    </div>
  );
};

export default GoodsReceivedNoteDetailsPage;
